// The saved document: read-only by default, editable in place.
import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildLogisticsCalculationSheetModel,
  type LogisticsCalculationDetail,
  type LogisticsCalculationDetailResponse,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { ConfirmModal, EditorPage, useEditorGuard, useNarrow } from '../../../ui/index.js';
import {
  LogisticsCalculationFormSchema,
  countPublicViews,
  persistedToEditableContent,
  toWireContent,
  type LogisticsCalculationFormValues,
} from '../form-model.js';
import { DocumentSheet } from '../sheet/DocumentSheet.js';
import {
  useLogisticsCalculationViews,
  usePatchLogisticsCalculation,
  useDeleteLogisticsCalculation,
  useRevokeLogisticsCalculation,
} from '../queries.js';
import { DocumentManagerToolbar } from './DocumentToolbar.js';
import { LogisticsCalculationForm } from './LogisticsCalculationForm.js';

export function LogisticsCalculationView({
  detail,
  onEdit,
  onBack,
}: {
  detail: LogisticsCalculationDetailResponse;
  onEdit: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const calculation = detail.calculation;
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = hasPermission(permissions, 'logisticsCalculations.manage');
  // Filling the sheet in needs the width the sheet is laid out for, so a narrow
  // screen is not offered an editor it could not use (#2883 FR-5).
  const narrow = useNarrow();
  const views = useLogisticsCalculationViews(calculation.id);
  const revoke = useRevokeLogisticsCalculation(calculation.id);
  const remove = useDeleteLogisticsCalculation(calculation.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const showToast = useToastStore((state) => state.show);
  const dto = useMemo(() => documentOf(calculation), [calculation]);
  const copyLink = async () => {
    if (!detail.publicLink) return;
    await navigator.clipboard.writeText(
      new URL(detail.publicLink.path, window.location.origin).toString(),
    );
    showToast({ tone: 'success', text: t('actions.linkCopied') });
  };
  return (
    <div className="doc-page" data-testid="rl-document-view">
      <DocumentManagerToolbar
        detail={detail}
        canManage={canManage}
        htmlViews={views.data ? countPublicViews(views.data.items).htmlViews : null}
        revoking={revoke.isPending}
        deleting={remove.isPending}
        onBack={onBack}
        onEdit={narrow ? null : onEdit}
        onCopyLink={() => void copyLink()}
        onRevoke={() => revoke.mutate()}
        onDelete={() => setConfirmingDelete(true)}
      />
      {confirmingDelete ? (
        <ConfirmModal
          title={t('delete.title', { code: calculation.publicCode })}
          text={t(calculation.revokedAt ? 'delete.textRevoked' : 'delete.text')}
          confirmLabel={t('actions.delete')}
          cancelLabel={t('actions.cancel')}
          danger
          onClose={() => setConfirmingDelete(false)}
          onConfirm={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                showToast({
                  tone: 'success',
                  text: t('delete.done', { code: calculation.publicCode }),
                });
                onBack();
              },
            })
          }
        />
      ) : null}
      {narrow ? (
        <p className="doc-narrow-notice" data-testid="rl-document-narrow-notice">
          {t('document.narrowNotice')}
        </p>
      ) : null}
      <DocumentSheet dto={dto} publicCode={calculation.publicCode} />
    </div>
  );
}

/**
 * INVARIANT: a published document reads from its frozen snapshot, so the page a
 * manager reviews is byte-for-byte what the customer's link resolves to.
 */
function documentOf(calculation: LogisticsCalculationDetail): LogisticsCalculationPublicDto {
  const source = calculation.snapshot ?? {
    content: { ...calculation.content, layoutVersion: 'v2' as const },
    calculation: calculation.calculation,
  };
  return buildLogisticsCalculationSheetModel(source.content, source.calculation, {
    publicCode: calculation.publicCode,
  });
}

export function LogisticsCalculationEditHost({
  detail,
  onDone,
  onBack,
}: {
  detail: LogisticsCalculationDetailResponse;
  onDone: () => void;
  onBack: () => void;
}) {
  const form = useForm<LogisticsCalculationFormValues>({
    resolver: zodResolver(LogisticsCalculationFormSchema),
    defaultValues: persistedToEditableContent(detail.calculation.content),
  });
  const patch = usePatchLogisticsCalculation(detail.calculation.id);
  const busy = patch.isPending;
  return (
    <EditorPage dirty={form.formState.isDirty} busy={busy} onConfirmClose={onDone}>
      <EditBody
        detail={detail}
        form={form}
        patch={patch}
        busy={busy}
        onDone={onDone}
        onBack={onBack}
      />
    </EditorPage>
  );
}

function EditBody({
  detail,
  form,
  patch,
  busy,
  onDone,
  onBack,
}: {
  detail: LogisticsCalculationDetailResponse;
  form: ReturnType<typeof useForm<LogisticsCalculationFormValues>>;
  patch: ReturnType<typeof usePatchLogisticsCalculation>;
  busy: boolean;
  onDone: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const { confirmDestructive } = useEditorGuard();
  return (
    <LogisticsCalculationForm
      form={form}
      savedDraft={{
        id: detail.calculation.id,
        publicCode: detail.calculation.publicCode,
        documentDate: detail.calculation.content.documentDate,
      }}
      busy={busy}
      submitLabel={t('actions.save')}
      // The rejection travels on: the form names and marks a cargo another
      // document holds, and leaves this one open to be corrected.
      onSubmit={(content) => patch.mutateAsync({ content: toWireContent(content) }).then(onDone)}
      onCancel={() => confirmDestructive(onDone)}
      onBack={onBack}
    />
  );
}
